// ==========================================
// NEW SEARCH ARCHITECTURE - Phase 2
// search-engine.js
//
// Composes search-normalizer, search-ranker, and index-loader into a
// single query API. This is the "brain" of the new architecture.
//
// Phase 2 adds brand detection: given a data-driven brand lexicon
// (assets/brand-lexicon.json, loaded by the caller and passed in as
// `brandLexicon`), the engine tags each query with the canonical
// brand it mentions (if any) and the "remainder" of the query once
// that brand's alias text is stripped out. The Router uses this to
// decide brand-only vs. specific-product queries - no brand name or
// collection URL is ever hardcoded here.
// ==========================================

(function (global) {
  "use strict";

  var NewSearch = (global.NewSearch = global.NewSearch || {});

  if (NewSearch.SearchEngine) {
    return;
  }

  /**
   * SearchEngine
   * options:
   *   - indexLoader: an instance of NewSearch.IndexLoader
   *   - fields: string[] of item fields to match against (default below)
   *   - aliasMap: optional flat alias/synonym map applied before normalizing
   *     (format/typo aliases, e.g. "airdops" -> "airdopes")
   *   - brandLexicon: optional array of { brand, aliases } used for brand
   *     detection (this is assets/brand-lexicon.json's "brands" array)
   *   - weights: optional ranker weight overrides
   *   - minScore: minimum score to consider a match "confident"
   */
  function SearchEngine(options) {
    options = options || {};
    this.indexLoader = options.indexLoader || null;
    this.fields = options.fields || [
      "title",
      "brand",
      "sku",
      "aliases",
      "compatibleModels",
      "hiddenSearchKeywords",
    ];
    this.aliasMap = options.aliasMap || {};
    this.brandLexicon = options.brandLexicon || [];
    this.weights = options.weights || undefined;
    this.minScore = typeof options.minScore === "number" ? options.minScore : 40;
  }

  /**
   * Scans a normalized query for a known brand's alias text. Returns
   * { brand, remainder } where `remainder` is the query with the
   * matched alias text removed (used to tell "boat" apart from
   * "boat airdopes 161"). Returns { brand: null, remainder: query }
   * when nothing in the lexicon matches.
   */
  SearchEngine.prototype._detectBrand = function (normalizedQuery) {
    var normalizer = NewSearch.normalizer;
    var list = this.brandLexicon;

    // Flatten every (brand, alias) pair and try the LONGEST alias text
    // first. Without this, a short alias that happens to be a
    // substring of a longer one (e.g. "boult" inside "goboult") would
    // match first and leave a spurious remainder ("go"), incorrectly
    // turning a brand-only query into what looks like a specific
    // product query.
    var candidates = [];
    list.forEach(function (entry) {
      var aliases = entry.aliases || [entry.brand];
      aliases.forEach(function (alias) {
        var normalizedAlias = normalizer ? normalizer.normalize(alias) : String(alias).toLowerCase();
        if (normalizedAlias) candidates.push({ brand: entry.brand, normalizedAlias: normalizedAlias });
      });
    });
    candidates.sort(function (a, b) {
      return b.normalizedAlias.length - a.normalizedAlias.length;
    });

    for (var i = 0; i < candidates.length; i++) {
      var normalizedAlias = candidates[i].normalizedAlias;
      if (normalizedQuery === normalizedAlias || normalizedQuery.indexOf(normalizedAlias) !== -1) {
        var remainder = normalizedQuery.split(normalizedAlias).join(" ").replace(/\s+/g, " ").trim();
        return { brand: candidates[i].brand, remainder: remainder };
      }
    }

    return { brand: null, remainder: normalizedQuery };
  };

  /**
   * Runs a search against the configured index.
   * Returns a Promise resolving to:
   *   {
   *     query, normalizedQuery,
   *     brand: string|null,            // canonical brand detected, if any
   *     remainder: string,             // query with brand alias text stripped
   *     isBrandOnlyQuery: boolean,     // brand detected AND nothing else in the query
   *     results: [{item, score}], best: {item, score} | null, confident: boolean
   *   }
   *
   * If the index cannot be loaded (or no indexLoader is configured),
   * resolves with an empty result set rather than throwing, so callers
   * can always fall back safely.
   */
  SearchEngine.prototype.search = function (rawQuery) {
    var self = this;
    var normalizer = NewSearch.normalizer;
    var ranker = NewSearch.ranker;

    var aliasedQuery = normalizer
      ? normalizer.applyAliases(rawQuery, this.aliasMap)
      : String(rawQuery || "");
    var normalizedQuery = normalizer ? normalizer.normalize(aliasedQuery) : aliasedQuery;

    var brandInfo = this._detectBrand(normalizedQuery);

    var indexPromise = this.indexLoader ? this.indexLoader.load() : Promise.resolve([]);

    return indexPromise.then(function (index) {
      var results = ranker ? ranker.rank(index || [], normalizedQuery, self.fields, self.weights) : [];
      var best = results.length ? results[0] : null;
      var second = results.length > 1 ? results[1] : null;

      // A "confident single product" match requires:
      //  - the score clears minScore, AND
      //  - the query has enough specificity (2+ tokens) - a single
      //    generic word (see the Partial test queries: "buds", "case",
      //    "left"...) should never resolve straight to one product,
      //  - the winner isn't tied with the runner-up - a tie means the
      //    catalog genuinely has multiple equally-good candidates, so
      //    routing should show the search results page (Case 3), not
      //    guess between them.
      var tokenCount = normalizedQuery ? normalizedQuery.split(" ").filter(Boolean).length : 0;
      var isTiedWithSecond = !!(second && second.score === best.score);
      var confident = !!(
        best &&
        best.score >= self.minScore &&
        tokenCount >= 2 &&
        !isTiedWithSecond
      );

      return {
        query: rawQuery,
        normalizedQuery: normalizedQuery,
        brand: brandInfo.brand,
        remainder: brandInfo.remainder,
        isBrandOnlyQuery: !!(brandInfo.brand && brandInfo.remainder === ""),
        results: results,
        best: best,
        confident: confident,
      };
    });
  };

  NewSearch.SearchEngine = SearchEngine;
})(window);
